import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { OutputContainerService } from '@app/modules/run-phase/services/output-container/output-container.service';
import { OutputUnitDsService } from '@app/core/data-services/output-unit-ds/output-unit-ds.service';
import { OuterInfo } from '@app/shared/models';
import { PrintLabelEntry, LabelPrintInfo, LabelType } from 'chronos-core-client';
import { BehaviorSubject } from 'rxjs';
import { filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { PrintLabelService } from '@app/shared/services/print-label/print-label.service';

@Component({
  selector: 'app-print-label',
  templateUrl: './print-label.component.html',
  styleUrls: ['./print-label.component.scss']
})
export class PrintLabelComponent implements OnInit, OnChanges {
  @Input() public printInfo: LabelPrintInfo;
  @Input() public collapsible = false;
  @Input() public numberOfCopies?: number;
  @Input() public isQuantityPerOuterValidationRequired: boolean;
  @Input() public isBobbin? = false;
  @Input() public productionOrderId?: number;
  @Input() public producedMaterialId?: number;
  @Input() public isPalletFinished?: boolean;
  @Input() public slitCount?: number;
  @Input() public palletQuantity?: number;
  @Output() public quantityChanged = new EventEmitter<LabelType>();

  public readonly INPUT_MIN_QUANTITY = 1;

  public isCollapsed: boolean;
  public isQuantityPerOuterValid = false;
  public quantityPerOuter: number;
  public isQuantityPerOuterChangeInProgress: boolean = false;
  public isPrintLabelRequestInProgress: boolean = false;
  private changePrintInfoInProgressSubject = new BehaviorSubject<boolean>(false);
  private changePrintInfoInProgress$ = this.changePrintInfoInProgressSubject.asObservable();

  constructor(
    private outputContainerService: OutputContainerService,
    private outputUnitDsService: OutputUnitDsService,
    private printLabelService: PrintLabelService
  ) {}

  public ngOnInit(): void {
    this.isCollapsed = this.collapsible;
    if (this.printInfo) {
      const info = {
        quantityPerOuter: this.printInfo.quantityPerOuter,
        numberCopies: this.printInfo.numberCopies,
        outerLabelType: this.printInfo.labelType
      };
      this.numberOfCopies = this.printInfo.numberCopies;
      this.quantityPerOuter = this.printInfo.quantityPerOuter;

      this.checkQuantityPerOuterValidation(info.quantityPerOuter);

      if (this.isBobbin) {
        this.printCalculationForBobbin();
      }
    } else {
      this.printInfo = {} as LabelPrintInfo;
    }
  }

  public ngOnChanges(): void {
    const info = {
      quantityPerOuter: this.printInfo.quantityPerOuter,
      numberCopies: this.printInfo.numberCopies,
      outerLabelType: this.printInfo.labelType
    };
    this.numberOfCopies = this.printInfo.numberCopies;
    this.quantityPerOuter = this.printInfo.quantityPerOuter;

    this.checkQuantityPerOuterValidation(info.quantityPerOuter);

    if (this.isBobbin) {
      this.printCalculationForBobbin();
    }
  }

  public checkQuantityPerOuterValidation(quantityPerOuter: number): boolean {
    if (this.isQuantityPerOuterValidationRequired) {
      return this.isQuantityPerOuterValid = quantityPerOuter ?? 0 > 0 ? true : false;
    } else {
      return this.isQuantityPerOuterValid = quantityPerOuter ? true : false;
    }
  }

  public toggleContent(isCollapsed: boolean): void {
    this.isCollapsed = isCollapsed;
  }

  public printLabel(printLabel: PrintLabelEntry): void {
    if (!this.productionOrderId) {
      console.error('Production Order Id is required to print label');
    }
    if (!this.checkQuantityPerOuterValidation(printLabel.quantityPerOuter)) {
      return;
    }
    this.isPrintLabelRequestInProgress = true;

    this.changePrintInfoInProgress$
      .pipe(
        filter(inProgress => !inProgress),
        take(1),
        switchMap(() => {
          return this.printLabelService.printLabel(this.productionOrderId, printLabel);
        }),
        tap(pi => this.printInfo = pi),
        finalize(() => {
          this.isPrintLabelRequestInProgress = false;
        })
      )
      .subscribe();
  }

  public printBobbinCoreLabel(printLabel: PrintLabelEntry): void {
    if (!this.producedMaterialId) {
      console.error('Produced Material Id is required to print bobbin/core label');
    }
    if (!this.checkQuantityPerOuterValidation(printLabel.quantityPerOuter)) {
      return;
    }
    this.isPrintLabelRequestInProgress = true;

    // Note: bobbins don't have a function to load the data actively, the data is updated together with its owning produced material.
    this.changePrintInfoInProgress$
      .pipe(
        filter(inProgress => !inProgress),
        take(1),
        switchMap(() => this.printLabelService.printBobbinCoreLabel(this.producedMaterialId, printLabel)),
        finalize(() => {
          this.isPrintLabelRequestInProgress = false;
        })
      )
      .subscribe();
  }

  public onQuantityPerOuterChange(info: OuterInfo): void {
    this.onNumberOfCopiesChanged(info);
  }

  public onNumberOfCopiesChanged(info: OuterInfo): void {
    this.changePrintInfoInProgressSubject.next(true);
    this.isQuantityPerOuterChangeInProgress = true;
    this.numberOfCopies = info.numberCopies;
    this.printInfo.quantityPerOuter = info.quantityPerOuter;

    if (info.producedMaterialId) {
      if (!this.producedMaterialId) {
        console.error('Produced Material Id is required to change quantity per outer or number of copies');
      }

      this.outputContainerService
        .changeProducedMaterialPrintInfo(
          info.producedMaterialId,
          this.printInfo.labelType,
          info.quantityPerOuter,
          info.numberCopies
        )
        .pipe(
          finalize(() => {
            this.changePrintInfoInProgressSubject.next(false);
            this.isQuantityPerOuterChangeInProgress = false;
            this.quantityChanged.emit(this.printInfo.labelType);
          })
        )
        .subscribe();
    } else {
      if (!this.productionOrderId) {
        console.error('Production Order Id is required to change quantity per outer or number of copies');
      }
      this.outputUnitDsService
        .saveQuantityPerOuterAndCopies(
          this.productionOrderId,
          this.printInfo.labelType,
          info.quantityPerOuter,
          info.numberCopies ?? this.INPUT_MIN_QUANTITY
        )
        .pipe(
          switchMap(() => this.printLabelService.getLabelPrintInfo(this.productionOrderId, this.printInfo.labelType)),
          tap(printInfo => {
            this.printInfo = printInfo;
            this.checkQuantityPerOuterValidation(printInfo.quantityPerOuter);
          }),
          finalize(() => {
            this.changePrintInfoInProgressSubject.next(false);
            this.isQuantityPerOuterChangeInProgress = false;
            this.quantityChanged.emit(this.printInfo.labelType);
          })
        )
        .subscribe();
    }
  }

  private printCalculationForBobbin() {
    this.printInfo.totalOutputUnits = this.getBobbinEstimation();
    this.numberOfCopies = this.numberOfCopies;
  }

  public getBobbinEstimation(): number {
    const palletQty = this.palletQuantity;
    const bobbinQty = this.printInfo.defaultQuantityPerOuter;
    if (
      typeof palletQty === 'number' &&
      typeof bobbinQty === 'number' &&
      typeof this.slitCount === 'number' &&
      bobbinQty !== 0 &&
      this.slitCount !== 0
    ) {
      return Math.ceil(palletQty / bobbinQty) * this.slitCount;
    } else {
      return 0;
    }
  }

  public onQuantityPerOuterInput(info: OuterInfo): void {
    this.checkQuantityPerOuterValidation(info.quantityPerOuter);
    this.quantityPerOuter = info.quantityPerOuter;
  }
}
